/**
 * Pure logic for stacked line-item discounts: reading a line's discounts,
 * grouping them across a ticket (the summary's one-row-per-discount list), and
 * building each line's NEXT full discount set when a discount is applied,
 * changed, or removed. The apply-discount modal and its hosts (Transaction
 * Detail, Quick Sale cart, package-sale review) stay thin by delegating here.
 *
 * Vocabulary:
 *  - a DiscountableLine is any ticket line a discount can go on, normalized
 *    across booked services, products, service packages and cart lines;
 *  - a LineDiscountEntry is one discount on a line (persisted with an `id`, or
 *    staged with only its preset);
 *  - a DiscountGroup is "the same discount" across lines — the same preset, or
 *    for legacy/unlinked discounts the same name + type + amount.
 */
import {
  DiscountType,
  LineDiscountEntry,
  LineItemDiscount,
  LineItemDiscountInput,
} from "../types/discounts";
import {
  BookedService,
  EditTransactionLineItemsRequest,
  GetTransactionsResponse,
  Product,
  TransactionResponse,
  TransactionServicePackage,
} from "../types/transactions";
import { getLineBase } from "./discountUtils";

export type DiscountableLineKind = "service" | "product" | "package";

export interface DiscountableLine {
  /** Unique within the ticket (a line_item_id, or a cart product id). */
  key: string;
  kind: DiscountableLineKind;
  name: string;
  /** Pre-discount base, for display. */
  base: number;
  /** Discounts currently on the line, in application order. */
  discounts: LineDiscountEntry[];
}

/**
 * Structured identity of a discount group: the preset, or (legacy/unlinked
 * discounts) the name + type + amount. Never flattened into a delimited string
 * that has to be split back apart.
 */
export type DiscountGroupKey =
  | { presetId: string }
  | { name: string; discount_type: string; amount: number };

export interface DiscountGroup {
  key: DiscountGroupKey;
  /** Serialized `key`, for React keys and lookups — never parsed. */
  id: string;
  presetId: string | null;
  name: string;
  discount_type: DiscountType;
  amount: number;
  /** Keys of the lines carrying this discount, in ticket order. */
  lineKeys: string[];
}

/** The discount being applied by the modal. */
export interface DiscountToApply {
  /** Source preset. Null only when re-valuing an unlinked group being changed. */
  presetId: string | null;
  name: string;
  discount_type: DiscountType;
  amount: number;
}

export interface ApplyDiscountPlan {
  discount: DiscountToApply;
  /** Lines ticked in the modal. */
  selectedLineKeys: Iterable<string>;
  /**
   * The group being changed ("Change" on a summary row). Its entries are
   * replaced on ticked lines and removed from lines that were unticked.
   */
  editingGroup?: DiscountGroup | null;
}

/** One line's next FULL discount set (replace semantics). */
export interface LineDiscountChange {
  lineKey: string;
  /** The line's discounts before the change. */
  previous: LineDiscountEntry[];
  /** The line's discounts after the change; kept entries keep their `id`. */
  discounts: LineDiscountEntry[];
}

// ---------------------------------------------------------------------------
// Reading a line's discounts
// ---------------------------------------------------------------------------

/** A line whose applied discounts can be read. */
export interface LineWithDiscounts {
  discounts?: LineItemDiscount[] | LineDiscountEntry[] | null;
}

const toEntry = (discount: LineDiscountEntry): LineDiscountEntry => ({
  ...(discount.id ? { id: discount.id } : {}),
  discount_preset_id: discount.discount_preset_id ?? null,
  name: discount.name,
  discount_type: discount.discount_type,
  amount: discount.amount,
});

/** A line's applied discounts as entries (without the dollar amounts). */
export const getLineDiscountEntries = (
  line: LineWithDiscounts,
): LineDiscountEntry[] => (line.discounts ?? []).map(toEntry);

// ---------------------------------------------------------------------------
// Grouping
// ---------------------------------------------------------------------------

export const discountGroupKeyOf = (
  entry: LineDiscountEntry,
): DiscountGroupKey =>
  entry.discount_preset_id
    ? { presetId: entry.discount_preset_id }
    : {
        name: entry.name,
        discount_type: entry.discount_type,
        amount: entry.amount,
      };

/** Serialized group key — JSON, so names containing any character are safe. */
export const discountGroupIdOf = (entry: LineDiscountEntry): string =>
  JSON.stringify(discountGroupKeyOf(entry));

/**
 * Groups the discounts on a ticket's lines: one group per preset (or per
 * name/type/amount for unlinked discounts), in order of first appearance.
 * A group's display values come from its first entry.
 */
export function groupLineDiscounts(lines: DiscountableLine[]): DiscountGroup[] {
  const groups = new Map<string, DiscountGroup>();
  for (const line of lines) {
    for (const entry of line.discounts) {
      const id = discountGroupIdOf(entry);
      const existing = groups.get(id);
      if (existing) {
        if (!existing.lineKeys.includes(line.key)) {
          existing.lineKeys.push(line.key);
        }
        continue;
      }
      groups.set(id, {
        key: discountGroupKeyOf(entry),
        id,
        presetId: entry.discount_preset_id,
        name: entry.name,
        discount_type: entry.discount_type,
        amount: entry.amount,
        lineKeys: [line.key],
      });
    }
  }
  return Array.from(groups.values());
}

/** The group a preset forms on this ticket, if it's applied anywhere. */
export const findGroupForPreset = (
  groups: DiscountGroup[],
  presetId: string,
): DiscountGroup | undefined =>
  groups.find((group) => group.presetId === presetId);

// ---------------------------------------------------------------------------
// Building next discount sets
// ---------------------------------------------------------------------------

/** True when two discount sets hold the same discounts, in the same order,
 *  with the same values. */
export const isSameDiscountSet = (
  a: LineDiscountEntry[],
  b: LineDiscountEntry[],
): boolean =>
  a.length === b.length &&
  a.every(
    (entry, i) =>
      entry.id === b[i].id &&
      entry.discount_preset_id === b[i].discount_preset_id &&
      entry.discount_type === b[i].discount_type &&
      entry.amount === b[i].amount,
  );

/**
 * Each affected line's next FULL discount set after applying `plan`:
 *
 * - A ticked line gets the discount. If the line already carries it (same
 *   preset — or, when re-valuing an unlinked group, that group's entry) the
 *   existing entry is re-valued in place and keeps its `id`; otherwise a new
 *   entry is appended. Every other discount on the line is kept.
 * - When changing a group, the group's entry is removed from lines that were
 *   unticked, and replaced on ticked lines if a different preset was chosen.
 *
 * Lines whose set doesn't change are omitted. An unlinked discount (no preset)
 * can't be added to a line that doesn't already carry it.
 */
export function buildNextLineDiscounts(
  lines: DiscountableLine[],
  plan: ApplyDiscountPlan,
): LineDiscountChange[] {
  const { discount, editingGroup } = plan;
  const selected = new Set(plan.selectedLineKeys);
  const groupId = editingGroup?.id ?? null;

  const isEditedGroupEntry = (entry: LineDiscountEntry) =>
    groupId !== null && discountGroupIdOf(entry) === groupId;
  const isTargetEntry = (entry: LineDiscountEntry) =>
    discount.presetId
      ? entry.discount_preset_id === discount.presetId
      : isEditedGroupEntry(entry);

  const changes: LineDiscountChange[] = [];
  for (const line of lines) {
    const isSelected = selected.has(line.key);
    if (!isSelected && !line.discounts.some(isEditedGroupEntry)) continue;

    const next: LineDiscountEntry[] = [];
    let applied = false;
    for (const entry of line.discounts) {
      if (isSelected && isTargetEntry(entry)) {
        // Re-value the first matching entry in place; drop any duplicate.
        if (!applied) {
          next.push({
            ...entry,
            discount_type: discount.discount_type,
            amount: discount.amount,
          });
          applied = true;
        }
        continue;
      }
      if (isEditedGroupEntry(entry)) continue; // unticked, or replaced
      next.push(entry);
    }
    if (isSelected && !applied && discount.presetId) {
      next.push({
        discount_preset_id: discount.presetId,
        name: discount.name,
        discount_type: discount.discount_type,
        amount: discount.amount,
      });
    }

    if (!isSameDiscountSet(line.discounts, next)) {
      changes.push({
        lineKey: line.key,
        previous: line.discounts,
        discounts: next,
      });
    }
  }
  return changes;
}

/** Each line's next discount set with `group` removed. */
export const buildRemoveGroupChanges = (
  lines: DiscountableLine[],
  group: DiscountGroup,
): LineDiscountChange[] =>
  buildNextLineDiscounts(lines, {
    discount: {
      presetId: null,
      name: group.name,
      discount_type: group.discount_type,
      amount: group.amount,
    },
    selectedLineKeys: [],
    editingGroup: group,
  });

/**
 * A line's next discount set as PUT /transactions/line-items entries:
 * an unchanged applied discount → `{id}`; a re-valued one →
 * `{id, discount_type, amount}`; a staged one →
 * `{discount_preset_id, discount_type, amount}`. A staged entry without a
 * preset can't be expressed and is dropped.
 */
export function toLineItemDiscountInputs(
  next: LineDiscountEntry[],
  previous: LineDiscountEntry[],
): LineItemDiscountInput[] {
  const inputs: LineItemDiscountInput[] = [];
  for (const entry of next) {
    if (entry.id) {
      const before = previous.find((p) => p.id === entry.id);
      const revalued =
        !before ||
        before.discount_type !== entry.discount_type ||
        before.amount !== entry.amount;
      inputs.push(
        revalued
          ? {
              id: entry.id,
              discount_type: entry.discount_type,
              amount: entry.amount,
            }
          : { id: entry.id },
      );
    } else if (entry.discount_preset_id) {
      inputs.push({
        discount_preset_id: entry.discount_preset_id,
        discount_type: entry.discount_type,
        amount: entry.amount,
      });
    }
  }
  return inputs;
}

// ---------------------------------------------------------------------------
// Transaction adapters
// ---------------------------------------------------------------------------

type AnyTransaction = GetTransactionsResponse | TransactionResponse;

const serviceLine = (service: BookedService): DiscountableLine => ({
  key: service.line_item_id,
  kind: "service",
  name: service.name,
  base: getLineBase(service),
  discounts: getLineDiscountEntries(service),
});

const productLine = (product: Product): DiscountableLine => ({
  key: product.line_item_id,
  kind: "product",
  name: product.name,
  base: getLineBase(product),
  discounts: getLineDiscountEntries(product),
});

const packageLine = (pkg: TransactionServicePackage): DiscountableLine => ({
  key: pkg.line_item_id,
  kind: "package",
  name: pkg.name,
  base: pkg.unit_price,
  discounts: getLineDiscountEntries(pkg),
});

/**
 * The ticket's discountable lines, keyed by line_item_id, in display order:
 * services, products, then packages. Gift-card lines can't be discounted.
 */
export const buildDiscountableLines = (
  transaction: AnyTransaction,
): DiscountableLine[] => [
  ...transaction.line_items.booked_services.map(serviceLine),
  ...transaction.line_items.products.map(productLine),
  ...(transaction.line_items.service_packages ?? []).map(packageLine),
];

/**
 * The PUT /transactions/line-items body for a set of line discount changes:
 * each changed line carries only its identity + its full `discounts` set.
 */
export function buildLineDiscountsRequest(
  transaction: AnyTransaction,
  changes: LineDiscountChange[],
): EditTransactionLineItemsRequest {
  const byKey = new Map(changes.map((change) => [change.lineKey, change]));
  const discountsFor = (lineItemId: string) => {
    const change = byKey.get(lineItemId);
    return change
      ? toLineItemDiscountInputs(change.discounts, change.previous)
      : null;
  };

  const bookedServices = transaction.line_items.booked_services.flatMap(
    (service) => {
      const discounts = discountsFor(service.line_item_id);
      return discounts
        ? [{ id: service.id, line_item_id: service.line_item_id, discounts }]
        : [];
    },
  );
  const products = transaction.line_items.products.flatMap((product) => {
    const discounts = discountsFor(product.line_item_id);
    return discounts
      ? [{ id: product.id, line_item_id: product.line_item_id, discounts }]
      : [];
  });
  const servicePackages = (
    transaction.line_items.service_packages ?? []
  ).flatMap((pkg) => {
    const discounts = discountsFor(pkg.line_item_id);
    return discounts ? [{ line_item_id: pkg.line_item_id, discounts }] : [];
  });

  return {
    transaction_id: transaction.transaction.id,
    line_items: {
      ...(bookedServices.length > 0 && { booked_services: bookedServices }),
      ...(products.length > 0 && { products }),
      ...(servicePackages.length > 0 && { service_packages: servicePackages }),
    },
  };
}

/**
 * The PUT body that removes `group` from every line it's on, keeping each
 * line's other discounts (summary row "Remove").
 */
export const buildRemoveDiscountGroupRequest = (
  transaction: AnyTransaction,
  group: DiscountGroup,
): EditTransactionLineItemsRequest =>
  buildLineDiscountsRequest(
    transaction,
    buildRemoveGroupChanges(buildDiscountableLines(transaction), group),
  );
